import {Injectable} from '@angular/core';
import {NgOpenCVService, OpenCVLoadResult} from 'ng-open-cv';
import {BehaviorSubject, forkJoin, Observable} from 'rxjs';
import {filter, switchMap} from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class FaceRecognitionService {
  public static LOCALLY_CNN = '#LOCALLY_CNN#';
  public static API_CNN = 'https://snf-873139.vm.okeanos.grnet.gr/emotionanalysis/uploader';
  public static API_BOVW = 'https://snf-873139.vm.okeanos.grnet.gr/emotiondetectionweka/prediction';
  public emotionServiceUrl = FaceRecognitionService.LOCALLY_CNN;

  public fps = 30;
  // Notifies of the ready state of the classifiers load operation
  private classifiersLoaded = new BehaviorSubject<boolean>(false);
  public started?: boolean;

  public classifiersLoadFinished = true;
  public onLoadedClassifiers: any;
  private bFoundFace?: boolean;

  constructor(private ngOpenCVService: NgOpenCVService) {
    this.prepareOpenCV();
  }

  get foundFace(): boolean {
    return this.bFoundFace ?? false;
  }

  private prepareOpenCV() {
    this.classifiersLoadFinished = false;
    this.ngOpenCVService.isReady$
      .pipe(
        // The OpenCV library has been successfully loaded if result.ready === true
        filter((result: OpenCVLoadResult) => result.ready),
        switchMap(() => {
          // Load the face and eye classifiers files
          console.log('Load the face and eye classifiers files');
          return this.loadClassifiers();
        })
      )
      .subscribe(() => {
        // The classifiers have been succesfully loaded
        console.log(' The classifiers have been succesfully loaded');
        this.classifiersLoaded.next(true);
        this.classifiersLoadFinished = true;
        setTimeout(() => {
          if (this.onLoadedClassifiers) {
            this.onLoadedClassifiers();
          }
        }, 1000);
      });
  }

  loadClassifiers(): Observable<any> {
    return forkJoin(
      this.ngOpenCVService.createFileFromUrl(
        'haarcascade_frontalface_alt.xml',
        `assets/opencv/data/haarcascades/haarcascade_frontalface_alt.xml`
      )
    );
  }

  // Video detection
  videoDetection(srcElement: any, dstElement: any, previewCanvas: any, uploadPhotoFunc: any) {
    if (srcElement == null || dstElement == null || previewCanvas == null || uploadPhotoFunc == null) {
      return;
    }

    this.bFoundFace = false;
    this.started = true;
    const video = srcElement;
    video.height = 480;
    video.width = 640;
    dstElement.height = video.height;
    dstElement.width = video.width;

    const uploadCanvas = previewCanvas;
    const uploadCanvasContext = previewCanvas.getContext('2d');

    const src = new cv.Mat(video.height, video.width, cv.CV_8UC4);
    const dst = new cv.Mat(video.height, video.height, cv.CV_8UC4);
    const gray = new cv.Mat();
    const cap = new cv.VideoCapture(video);
    const faces = new cv.RectVector();
    const classifier = new cv.CascadeClassifier();
    // load pre-trained classifiers
    classifier.load('haarcascade_frontalface_alt.xml');
    let uploadTimestamp = 0;

    let lastCropped: any;
    const myRecursionFunc = (delay: number) => {
      setTimeout(() => {
        try {
          if (!this.started) {
            // clean and stop.
            src.delete();
            dst.delete();
            gray.delete();
            faces.delete();
            classifier.delete();
            return;
          }
          const begin = Date.now();
          cap.read(src);
          src.copyTo(dst);
          cv.cvtColor(dst, gray, cv.COLOR_RGBA2GRAY, 0);
          classifier.detectMultiScale(gray, faces, 1.3, 5, 0);
          lastCropped = undefined;
          if (faces.size() > 0) {
            this.bFoundFace = true;
            const face = faces.get(0);
            const limitX = dst.cols;
            const limitY = dst.rows;
            this.drawRectangle(face, dst, face.width * 0.1, face.height * 0.1, limitX, limitY);
            lastCropped = new cv.Mat();
            if (this.emotionServiceUrl === FaceRecognitionService.LOCALLY_CNN) {
              lastCropped = this.getCropped(face, src, 0, 0, limitX, limitY);
              cv.cvtColor(lastCropped, lastCropped, cv.COLOR_RGBA2GRAY, 0);
              const size = new cv.Size(48, 48);
              cv.resize(lastCropped, lastCropped, size);
            } else if (this.emotionServiceUrl === FaceRecognitionService.API_CNN) {
              lastCropped = this.getCropped(face, src, face.width * 0.3, face.height * 0.3, limitX, limitY);
            } else /* if (this.emotionServiceUrl === FaceRecognitionService.API_BOVW)*/ {
              lastCropped = this.getCropped(face, src, face.width * 0.1, face.height * 0.1, limitX, limitY);
            }
          } else {
            this.bFoundFace = false;
          }
          // this.clearCanvas(uploadCanvasContext, dstElement);
          cv.imshow(dstElement, dst);
          if (begin > uploadTimestamp) {
            if (lastCropped != null) {
              uploadTimestamp = begin + 1000;
              this.clearCanvas(uploadCanvasContext, uploadCanvas);
              cv.imshow(uploadCanvas, lastCropped);
              uploadPhotoFunc(uploadCanvas);
            } else {
              // this.clearCanvas(uploadCanvasContext, uploadCanvas);
              this.drawCanvasBorder(uploadCanvasContext, uploadCanvas, '#FF0000');
              // this.drawCanvasX(uploadCanvasContext, uploadCanvas, '#FF0000');
            }
          }

          // schedule the next one.
          delay = 1000 / this.fps; // - (Date.now() - begin);
          // setTimeout(processVideo, delay);
          myRecursionFunc(delay);
        } catch (err) {
          console.log(err);
        }
      }, delay);
    };

    myRecursionFunc(0);
  }

  private drawRectangle(face: any, dst: any, plusFrameWidth: any, plusFrameHeight: any, limitX: any, limitY: any) {
    let pointAX = Math.floor(face.x - plusFrameWidth);
    let pointAY = Math.floor(face.y - plusFrameHeight);

    let pointBX = Math.floor(face.x + plusFrameWidth + face.width);
    let pointBY = Math.floor(face.y + plusFrameHeight + face.height);

    if (pointAX < 0) {
      pointAX = 0;
      // pointBX -= pointAX;
    }
    if (pointAY < 0) {
      pointAY = 0;
      // pointBY -= pointAY;
    }
    if (pointBX > limitX) {
      pointBX = limitX;
    }
    if (pointBY > limitY) {
      pointBY = limitY;
    }

    const point1 = new cv.Point(pointAX, pointAY);
    const point2 = new cv.Point(pointBX, pointBY);
    cv.rectangle(dst, point1, point2, [255, 0, 0, 255]);
  }

  private getCropped(face: any, dst: any, plusFrameWidth: any, plusFrameHeight: any, limitX: any, limitY: any) {
    let rectAX = Math.floor(face.x - plusFrameWidth + 1);
    let rectAY = Math.floor(face.y - plusFrameHeight + 1);

    let rectBX = Math.floor((2 * plusFrameWidth) + face.width - 2);
    let rectBY = Math.floor((2 * plusFrameHeight) + face.height - 2);

    // console.log()
    if (rectAX + rectBX > limitX) {
      rectBX = limitX - rectAX;
    }
    if (rectAX < 0) {
      rectAX = 0;
    }
    if (rectAY + rectBY > limitY) {
      rectBY = limitY - rectAY;
    }
    if (rectAY < 0) {
      rectAY = 0;
    }

    const rect = new cv.Rect(rectAX, rectAY, rectBX, rectBY);
    return dst.roi(rect);
  }

  stopVideoDetection() {
    this.started = false;
  }

  clearCanvas(canvasContext: any, canvas: any) {
    canvasContext.clearRect(0, 0, canvas.width, canvas.height);
  }

  drawCanvasBorder(canvasContext: any, canvas: any, color: any) {
    canvasContext.strokeStyle = color;
    canvasContext.rect(0, 0, canvas.width, canvas.height);
    canvasContext.stroke();
  }

  drawCanvasX(canvasContext: any, canvas: any, color: any) {
    canvasContext.strokeStyle = color;
    canvasContext.moveTo(0, 0);
    canvasContext.lineTo(canvas.width, canvas.height);
    canvasContext.moveTo(canvas.width, 0);
    canvasContext.lineTo(0, canvas.height);
    canvasContext.stroke();
  }
}
